import { useState, useRef, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { StatsCard } from "@/components/stats-card";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Terminal,
  RefreshCw,
  Edit3,
  History,
  CheckCircle2,
  XCircle,
  Copy,
  Sparkles,
  Loader2,
  AlertTriangle,
  Hash,
  Cpu,
  Coins,
  Save,
  ArrowLeft,
} from "lucide-react";
import { promptApi, type Prompt, type TemplateVariable, type PromptRun } from "@/api/prompt-management";
import PromptPipeline from "@/components/prompt-pipeline";

// ── AI Model options (synced with Straico model list 2026-04) ──
// Cost tiers based on Straico coins/100 words:
//   Very Low = 0.1~0.5 | Low = 1 | Mid = 2~5 | High = 6~12

const AI_MODELS = [
  // ── GPT-5 系列（⭐⭐⭐ 編輯推薦）──
  { value: "openai/gpt-5-mini",  label: "GPT-5 Mini ⭐⭐⭐",  cost: "Low (1幣)" },
  { value: "openai/gpt-5",       label: "GPT-5 ⭐⭐⭐",        cost: "High (8幣)" },
  { value: "openai/gpt-5-nano",  label: "GPT-5 Nano ⭐⭐⭐",   cost: "Very Low (0.5幣)" },
  // ── GPT-4.1 系列 ──
  { value: "openai/gpt-4.1-mini", label: "GPT-4.1 Mini",      cost: "Low (1幣)" },
  { value: "openai/gpt-4.1",      label: "GPT-4.1",           cost: "Mid (3幣)" },
  { value: "openai/gpt-4.1-nano", label: "GPT-4.1 Nano",      cost: "Very Low (0.5幣)" },
  // ── DeepSeek（⭐⭐⭐ 寫作/內容）──
  { value: "deepseek/deepseek-chat-v3.1",  label: "DeepSeek V3.1 ⭐⭐⭐", cost: "Low (2幣)" },
  { value: "deepseek/deepseek-chat-v3-0324", label: "DeepSeek V3 0324",  cost: "Low (1幣)" },
  // ── Google Gemini ──
  { value: "google/gemini-3-flash-preview", label: "Gemini 3 Flash ⭐⭐⭐", cost: "Mid (2幣)" },
  { value: "google/gemini-2.5-flash-lite",  label: "Gemini 2.5 Flash Lite", cost: "Very Low (0.5幣)" },
  // ── Anthropic Claude ──
  { value: "anthropic/claude-sonnet-4-5",  label: "Claude Sonnet 4.5 ⭐⭐⭐", cost: "High (10幣)" },
  { value: "anthropic/claude-haiku-4-5",   label: "Claude Haiku 4.5",        cost: "Mid (4幣)" },
  // ── xAI ──
  { value: "x-ai/grok-4-fast",            label: "Grok 4 Fast",             cost: "Low (1幣)" },
];

// ── Step metadata ──

const STEP_INFO: Record<number, { label: string; color: string }> = {
  3: { label: "Step 3: 競品分析 Competitor", color: "bg-blue-100 text-blue-800" },
  4: { label: "Step 4: 用戶意圖 Intent",     color: "bg-green-100 text-green-800" },
  5: { label: "Step 5: 機會識別 Opportunity", color: "bg-purple-100 text-purple-800" },
  6: { label: "Step 6: 內容大綱 Outline",    color: "bg-orange-100 text-orange-800" },
};

// ── Main page component ──

export default function PromptManager() {
  const queryClient = useQueryClient();
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [historyStep, setHistoryStep] = useState<number | null>(null);
  const [compareIds, setCompareIds] = useState<[number, number] | null>(null);
  const [runsStep, setRunsStep] = useState<number | null>(null);

  // Fetch all active prompts
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["prompts"],
    queryFn: async () => {
      const res = await promptApi.list();
      return res.data as Prompt[];
    },
  });

  const allPrompts = data ?? [];
  // Group by step: latest active version per step
  const activePrompts = allPrompts
    .filter(p => p.is_active)
    .reduce<Record<number, Prompt>>((acc, p) => {
      if (!acc[p.step_number] || p.version > acc[p.step_number].version) {
        acc[p.step_number] = p;
      }
      return acc;
    }, {});

  const promptCards = Object.values(activePrompts).sort((a, b) => a.step_number - b.step_number);
  const totalActive = promptCards.length;
  const totalVersions = allPrompts.length;
  const totalUsage = allPrompts.reduce((s, p) => s + (p.usage_count || 0), 0);

  if (error) {
    return (
      <div className="p-6 space-y-4">
        <h1 className="text-2xl font-bold">AI Prompt 管理</h1>
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
            <p className="text-lg font-medium">無法載入 Prompt 資料</p>
            <p className="text-sm text-muted-foreground mt-2">{(error as Error).message}</p>
            <Button onClick={() => refetch()} variant="outline" className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />重試
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Terminal className="h-6 w-6" />
            AI Prompt 管理
          </h1>
          <p className="text-sm text-muted-foreground">
            管理 SERP 分析 Pipeline 的 LLM 指令模板
          </p>
        </div>
        <Button onClick={() => refetch()} variant="outline" size="sm" disabled={isLoading}>
          <RefreshCw className={cn("h-4 w-4 mr-1", isLoading && "animate-spin")} />
          重新整理
        </Button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard title="Prompt 總數" value={totalVersions} icon={Hash} color="primary" />
        <StatsCard title="活躍中" value={totalActive} icon={CheckCircle2} color="success" />
        <StatsCard title="總版本數" value={totalVersions} icon={History} color="info" />
        <StatsCard title="總使用次數" value={totalUsage} icon={Sparkles} color="warning" />
      </div>

      {/* Pipeline visualization */}
      <PromptPipeline
        prompts={allPrompts}
        onEditPrompt={(prompt) => setEditingPrompt(prompt)}
      />

      {/* Prompt cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">載入中…</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {promptCards.map(prompt => (
            <PromptCard
              key={prompt.id}
              prompt={prompt}
              allVersions={allPrompts.filter(p => p.step_number === prompt.step_number)}
              onEdit={() => setEditingPrompt(prompt)}
              onShowHistory={() => setHistoryStep(prompt.step_number)}
              onShowRuns={() => setRunsStep(prompt.step_number)}
            />
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      {editingPrompt && (
        <EditPromptDialog
          prompt={editingPrompt}
          onClose={() => setEditingPrompt(null)}
          onSaved={() => {
            setEditingPrompt(null);
            queryClient.invalidateQueries({ queryKey: ["prompts"] });
          }}
        />
      )}

      {/* Version History Dialog */}
      {historyStep !== null && (
        <VersionHistoryDialog
          stepNumber={historyStep}
          versions={allPrompts
            .filter(p => p.step_number === historyStep)
            .sort((a, b) => b.version - a.version)}
          onClose={() => { setHistoryStep(null); setCompareIds(null); }}
          compareIds={compareIds}
          onCompare={setCompareIds}
          onRollback={(prompt) => {
            setHistoryStep(null);
            setEditingPrompt(prompt);
          }}
        />
      )}

      {/* Runs Dialog */}
      {runsStep !== null && (
        <RunsDialog
          stepNumber={runsStep}
          stepName={STEP_INFO[runsStep]?.label || `Step ${runsStep}`}
          onClose={() => setRunsStep(null)}
        />
      )}
    </div>
  );
}

// ── Prompt Card ──

function PromptCard({
  prompt,
  allVersions,
  onEdit,
  onShowHistory,
  onShowRuns,
}: {
  prompt: Prompt;
  allVersions: Prompt[];
  onEdit: () => void;
  onShowHistory: () => void;
  onShowRuns: () => void;
}) {
  const stepInfo = STEP_INFO[prompt.step_number] || { label: `Step ${prompt.step_number}`, color: "bg-gray-100 text-gray-800" };
  const variables = (prompt.template_variables || []) as TemplateVariable[];
  const modelInfo = AI_MODELS.find(m => m.value === prompt.ai_model);

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <Badge className={cn("text-xs", stepInfo.color)}>{stepInfo.label}</Badge>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-[10px] font-mono">v{prompt.version}</Badge>
            {prompt.is_active ? (
              <CheckCircle2 className="h-4 w-4 text-green-500" />
            ) : (
              <XCircle className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
        <CardTitle className="text-base mt-2">{prompt.name}</CardTitle>
        {prompt.description && (
          <p className="text-xs text-muted-foreground">{prompt.description}</p>
        )}
      </CardHeader>

      <CardContent className="flex-1 space-y-4">
        {/* Model */}
        <div className="flex items-center gap-2">
          <Cpu className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm font-mono">{modelInfo?.label || prompt.ai_model}</span>
          {modelInfo && (
            <Badge variant="outline" className="text-[10px]">{modelInfo.cost}</Badge>
          )}
        </div>

        {/* System prompt preview */}
        <div>
          <Label className="text-xs text-muted-foreground">System Prompt</Label>
          <pre className="mt-1 text-xs bg-muted rounded p-2 whitespace-pre-wrap max-h-24 overflow-auto font-mono">
            {prompt.system_prompt.slice(0, 200)}
            {prompt.system_prompt.length > 200 && "…"}
          </pre>
        </div>

        {/* User template preview */}
        <div>
          <Label className="text-xs text-muted-foreground">User Prompt Template</Label>
          <pre className="mt-1 text-xs bg-muted rounded p-2 whitespace-pre-wrap max-h-20 overflow-auto font-mono">
            {prompt.user_prompt_template.slice(0, 150)}
            {prompt.user_prompt_template.length > 150 && "…"}
          </pre>
        </div>

        {/* Variables */}
        {variables.length > 0 && (
          <div>
            <Label className="text-xs text-muted-foreground">可用變數</Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {variables.map(v => (
                <VariableTag key={v.name} variable={v} />
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
          <span>使用 {prompt.usage_count || 0} 次</span>
          {prompt.avg_cost != null && prompt.avg_cost > 0 && (
            <span className="flex items-center gap-1">
              <Coins className="h-3 w-3" />
              平均 {Number(prompt.avg_cost).toFixed(4)} Credits
            </span>
          )}
          <span>共 {allVersions.length} 版</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button size="sm" onClick={onEdit}>
            <Edit3 className="h-3.5 w-3.5 mr-1" />編輯
          </Button>
          <Button size="sm" variant="outline" onClick={onShowHistory}>
            <History className="h-3.5 w-3.5 mr-1" />版本歷史
          </Button>
          <Button size="sm" variant="outline" onClick={onShowRuns}>
            <Terminal className="h-3.5 w-3.5 mr-1" />執行紀錄
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Variable Tag (click to copy) ──

function VariableTag({ variable }: { variable: TemplateVariable }) {
  const handleCopy = () => {
    navigator.clipboard.writeText(`\${${variable.name}}`);
    toast.success(`已複製 \${${variable.name}}`);
  };

  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded text-[11px] font-mono hover:bg-blue-100 transition-colors cursor-pointer"
      title={`${variable.description}${variable.example ? ` (e.g. ${variable.example})` : ''}\nClick to copy`}
    >
      <Copy className="h-2.5 w-2.5" />
      ${`{${variable.name}}`}
    </button>
  );
}

// ── Edit Prompt Dialog ──

function EditPromptDialog({
  prompt,
  onClose,
  onSaved,
}: {
  prompt: Prompt;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [activeTab, setActiveTab] = useState<string>("system");
  const [systemPrompt, setSystemPrompt] = useState(prompt.system_prompt);
  const [userTemplate, setUserTemplate] = useState(prompt.user_prompt_template);
  const [aiModel, setAiModel] = useState(prompt.ai_model);
  const [outputFormat, setOutputFormat] = useState(prompt.output_format);
  const [changeNote, setChangeNote] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const variables = (prompt.template_variables || []) as TemplateVariable[];

  const createMutation = useMutation({
    mutationFn: () =>
      promptApi.create({
        step_number: prompt.step_number,
        name: prompt.name,
        system_prompt: systemPrompt,
        user_prompt_template: userTemplate,
        output_format: outputFormat,
        output_tag: prompt.output_tag || undefined,
        ai_model: aiModel,
        description: changeNote || prompt.description || undefined,
      }),
    onSuccess: () => {
      toast.success(`已儲存新版本 v${prompt.version + 1}`);
      onSaved();
    },
    onError: (err: Error) => {
      toast.error(`儲存失敗: ${err.message}`);
    },
  });

  const insertVariable = useCallback((varName: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const text = `\${${varName}}`;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = activeTab === "system" ? systemPrompt : userTemplate;
    const newValue = currentValue.slice(0, start) + text + currentValue.slice(end);

    if (activeTab === "system") {
      setSystemPrompt(newValue);
    } else {
      setUserTemplate(newValue);
    }

    // Restore cursor after React re-render
    requestAnimationFrame(() => {
      textarea.selectionStart = textarea.selectionEnd = start + text.length;
      textarea.focus();
    });
  }, [activeTab, systemPrompt, userTemplate]);

  const hasChanges =
    systemPrompt !== prompt.system_prompt ||
    userTemplate !== prompt.user_prompt_template ||
    aiModel !== prompt.ai_model ||
    outputFormat !== prompt.output_format;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] w-full h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            編輯 — {prompt.name}
            <Badge variant="secondary" className="ml-2">v{prompt.version}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex gap-4 min-h-0 overflow-hidden">
          {/* Left: Editor */}
          <div className="flex-1 flex flex-col min-w-0">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
              <TabsList>
                <TabsTrigger value="system">System Prompt</TabsTrigger>
                <TabsTrigger value="user">User Template</TabsTrigger>
              </TabsList>

              <TabsContent value="system" className="flex-1 mt-2">
                <Textarea
                  ref={activeTab === "system" ? textareaRef : undefined}
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  className="h-full min-h-[400px] font-mono text-sm resize-none"
                  placeholder="System prompt..."
                />
              </TabsContent>

              <TabsContent value="user" className="flex-1 mt-2">
                <Textarea
                  ref={activeTab === "user" ? textareaRef : undefined}
                  value={userTemplate}
                  onChange={e => setUserTemplate(e.target.value)}
                  className="h-full min-h-[400px] font-mono text-sm resize-none"
                  placeholder="User prompt template with ${variables}..."
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Right: Variables panel */}
          <div className="w-72 border rounded-lg flex flex-col shrink-0">
            <div className="p-3 border-b bg-muted/30">
              <h3 className="font-medium text-sm">可用變數</h3>
              <p className="text-xs text-muted-foreground">點擊插入到游標位置</p>
            </div>
            <ScrollArea className="flex-1 p-3">
              <div className="space-y-3">
                {variables.length === 0 ? (
                  <p className="text-xs text-muted-foreground">此 Prompt 無預定義變數</p>
                ) : (
                  variables.map(v => (
                    <button
                      key={v.name}
                      onClick={() => insertVariable(v.name)}
                      className="w-full text-left p-2 rounded border hover:bg-blue-50 hover:border-blue-300 transition-colors"
                    >
                      <div className="font-mono text-xs text-blue-700">${`{${v.name}}`}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{v.description}</div>
                      {v.example && (
                        <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                          e.g. {v.example}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>

              <Separator className="my-4" />

              {/* Settings */}
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">AI Model</Label>
                  <Select value={aiModel} onValueChange={setAiModel}>
                    <SelectTrigger className="mt-1 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AI_MODELS.map(m => (
                        <SelectItem key={m.value} value={m.value} className="text-xs">
                          {m.label} ({m.cost})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">Output Format</Label>
                  <Select value={outputFormat} onValueChange={v => setOutputFormat(v as 'markdown' | 'json' | 'xml')}>
                    <SelectTrigger className="mt-1 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="markdown">Markdown</SelectItem>
                      <SelectItem value="json">JSON</SelectItem>
                      <SelectItem value="xml">XML</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs">變更備註</Label>
                  <Input
                    value={changeNote}
                    onChange={e => setChangeNote(e.target.value)}
                    placeholder="描述此次變更..."
                    className="mt-1 h-8 text-xs"
                  />
                </div>
              </div>
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={!hasChanges || createMutation.isPending}
          >
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            儲存新版本 (v{prompt.version + 1})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Simple line-by-line diff ──

function computeLineDiff(oldText: string, newText: string): Array<{ type: 'same' | 'add' | 'remove'; line: string }> {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  const result: Array<{ type: 'same' | 'add' | 'remove'; line: string }> = [];

  // Simple LCS-based diff
  const m = oldLines.length;
  const n = newLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = oldLines[i - 1] === newLines[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  // Backtrack
  const diffs: Array<{ type: 'same' | 'add' | 'remove'; line: string }> = [];
  let i = m, j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      diffs.push({ type: 'same', line: oldLines[i - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      diffs.push({ type: 'add', line: newLines[j - 1] });
      j--;
    } else {
      diffs.push({ type: 'remove', line: oldLines[i - 1] });
      i--;
    }
  }

  return diffs.reverse();
}

function DiffView({ oldPrompt, newPrompt }: { oldPrompt: Prompt; newPrompt: Prompt }) {
  const [diffField, setDiffField] = useState<'system' | 'user'>('system');
  const oldText = diffField === 'system' ? oldPrompt.system_prompt : oldPrompt.user_prompt_template;
  const newText = diffField === 'system' ? newPrompt.system_prompt : newPrompt.user_prompt_template;
  const lines = computeLineDiff(oldText, newText);

  const addCount = lines.filter(l => l.type === 'add').length;
  const removeCount = lines.filter(l => l.type === 'remove').length;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Badge variant="outline">v{oldPrompt.version}</Badge>
        <span className="text-xs text-muted-foreground">→</span>
        <Badge variant="outline">v{newPrompt.version}</Badge>
        <span className="text-xs text-green-600">+{addCount}</span>
        <span className="text-xs text-red-600">-{removeCount}</span>
        <div className="ml-auto flex gap-1">
          <Button
            size="sm" variant={diffField === 'system' ? 'default' : 'outline'}
            className="h-6 text-[10px]"
            onClick={() => setDiffField('system')}
          >System</Button>
          <Button
            size="sm" variant={diffField === 'user' ? 'default' : 'outline'}
            className="h-6 text-[10px]"
            onClick={() => setDiffField('user')}
          >User Template</Button>
        </div>
      </div>
      <div className="max-h-64 overflow-auto border rounded bg-muted/20 p-2 font-mono text-xs">
        {lines.map((line, idx) => (
          <div
            key={idx}
            className={cn(
              "whitespace-pre-wrap px-1",
              line.type === 'add' && "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300",
              line.type === 'remove' && "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 line-through",
            )}
          >
            <span className="select-none text-muted-foreground mr-2 inline-block w-4 text-right">
              {line.type === 'add' ? '+' : line.type === 'remove' ? '-' : ' '}
            </span>
            {line.line || '\u00A0'}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Version History Dialog ──

function VersionHistoryDialog({
  stepNumber,
  versions,
  onClose,
  compareIds,
  onCompare,
  onRollback,
}: {
  stepNumber: number;
  versions: Prompt[];
  onClose: () => void;
  compareIds: [number, number] | null;
  onCompare: (ids: [number, number] | null) => void;
  onRollback: (prompt: Prompt) => void;
}) {
  const stepInfo = STEP_INFO[stepNumber] || { label: `Step ${stepNumber}`, color: "" };
  const [selectedForCompare, setSelectedForCompare] = useState<number[]>([]);

  const toggleCompare = (id: number) => {
    setSelectedForCompare(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 2) return [prev[1], id];
      return [...prev, id];
    });
  };

  const compareVersions = selectedForCompare.length === 2
    ? [versions.find(v => v.id === selectedForCompare[0]), versions.find(v => v.id === selectedForCompare[1])]
    : null;

  // Sort for diff: older version first
  const sortedCompare = compareVersions && compareVersions[0] && compareVersions[1]
    ? (compareVersions[0].version < compareVersions[1].version
        ? [compareVersions[0], compareVersions[1]]
        : [compareVersions[1], compareVersions[0]]) as [Prompt, Prompt]
    : null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            版本歷史 — {stepInfo.label}
          </DialogTitle>
        </DialogHeader>

        {/* Diff view */}
        {sortedCompare && (
          <div className="border rounded-lg p-4 bg-muted/20">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-sm">版本差異 Diff</h4>
              <Button size="sm" variant="ghost" onClick={() => setSelectedForCompare([])}>
                <XCircle className="h-3.5 w-3.5 mr-1" />清除比較
              </Button>
            </div>
            <DiffView oldPrompt={sortedCompare[0]} newPrompt={sortedCompare[1]} />
          </div>
        )}

        <ScrollArea className="flex-1 pr-2">
          <div className="space-y-3">
            {versions.map(v => {
              const isSelected = selectedForCompare.includes(v.id);
              return (
                <Card key={v.id} className={cn(isSelected && "ring-2 ring-blue-400")}>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={v.is_active ? "default" : "outline"}>v{v.version}</Badge>
                        {v.is_active && <Badge className="bg-green-100 text-green-800 text-[10px]">Active</Badge>}
                        <span className="text-xs font-mono text-muted-foreground">{v.ai_model}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {new Date(v.created_at).toLocaleString("zh-TW")}
                        </span>
                        <Button
                          size="sm"
                          variant={isSelected ? "default" : "outline"}
                          className="h-7 text-xs"
                          onClick={() => toggleCompare(v.id)}
                        >
                          比較
                        </Button>
                        {!v.is_active && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => onRollback(v)}
                            title="以此版本為基礎建立新版本"
                          >
                            <ArrowLeft className="h-3 w-3 mr-1" />回滾
                          </Button>
                        )}
                      </div>
                    </div>
                    {v.description && (
                      <p className="text-xs text-muted-foreground mt-2">{v.description}</p>
                    )}
                    <pre className="text-xs bg-muted rounded p-2 whitespace-pre-wrap max-h-24 overflow-auto font-mono mt-2">
                      {v.system_prompt.slice(0, 300)}{v.system_prompt.length > 300 && "…"}
                    </pre>
                    <div className="flex items-center gap-4 text-[10px] text-muted-foreground mt-2">
                      <span>使用 {v.usage_count || 0} 次</span>
                      {v.avg_cost != null && <span>平均 {Number(v.avg_cost).toFixed(4)} Credits</span>}
                      {v.avg_duration_ms != null && <span>{v.avg_duration_ms}ms</span>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// ── Runs Dialog (execution history with real input/output) ──

function RunsDialog({
  stepNumber,
  stepName,
  onClose,
}: {
  stepNumber: number;
  stepName: string;
  onClose: () => void;
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["prompt-runs", stepNumber],
    queryFn: async () => {
      const res = await promptApi.runs(stepNumber, 50);
      return res.data;
    },
  });

  const runs = data?.runs ?? [];

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`已複製 ${label}`);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Terminal className="h-5 w-5" />
            執行紀錄 — {stepName}
            <Badge variant="secondary">{runs.length} 筆</Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 -mx-6 px-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : runs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">尚無執行紀錄</div>
          ) : (
            <div className="space-y-3">
              {runs.map((run) => {
                const isExpanded = expandedId === run.id;
                return (
                  <Card key={run.id} className="overflow-hidden">
                    {/* Summary row */}
                    <div
                      className="flex items-center gap-4 p-3 cursor-pointer hover:bg-muted/50"
                      onClick={() => setExpandedId(isExpanded ? null : run.id)}
                    >
                      <Badge
                        variant={run.status === "completed" ? "default" : "destructive"}
                        className={run.status === "completed" ? "bg-green-600" : ""}
                      >
                        {run.status === "completed" ? "OK" : "FAIL"}
                      </Badge>
                      <span className="text-sm font-medium truncate max-w-48">
                        {run.keyword || "—"}
                      </span>
                      <span className="text-xs font-mono text-muted-foreground">{run.ai_model}</span>
                      <span className="text-xs text-muted-foreground">v{run.prompt_version}</span>
                      {run.cost != null && (
                        <span className="text-xs flex items-center gap-1">
                          <Coins className="h-3 w-3" />{Number(run.cost).toFixed(2)}
                        </span>
                      )}
                      {run.duration_ms != null && (
                        <span className="text-xs text-muted-foreground">
                          {(run.duration_ms / 1000).toFixed(1)}s
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground ml-auto">
                        {run.created_at ? new Date(run.created_at).toLocaleString("zh-TW") : "—"}
                      </span>
                    </div>

                    {/* Expanded: full prompt + output */}
                    {isExpanded && (
                      <div className="border-t p-4 space-y-4 bg-muted/30">
                        {/* Metadata */}
                        <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                          <span>Tokens: {run.tokens_input ?? 0} in / {run.tokens_output ?? 0} out</span>
                          <span>Request: {run.request_id?.slice(0, 8)}...</span>
                          {run.error_message && (
                            <span className="text-destructive">Error: {run.error_message.slice(0, 100)}</span>
                          )}
                        </div>

                        {/* System Prompt */}
                        {run.system_prompt && (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-xs font-bold">System Prompt（含真實資料）</Label>
                              <Button size="sm" variant="ghost" className="h-6 text-xs"
                                onClick={() => copyText(run.system_prompt!, "System Prompt")}>
                                <Copy className="h-3 w-3 mr-1" />複製
                              </Button>
                            </div>
                            <pre className="text-xs bg-background rounded p-3 whitespace-pre-wrap max-h-60 overflow-auto font-mono border">
                              {run.system_prompt}
                            </pre>
                          </div>
                        )}

                        {/* User Prompt */}
                        {run.user_prompt && (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-xs font-bold">User Prompt（含真實資料）</Label>
                              <Button size="sm" variant="ghost" className="h-6 text-xs"
                                onClick={() => copyText(run.user_prompt!, "User Prompt")}>
                                <Copy className="h-3 w-3 mr-1" />複製
                              </Button>
                            </div>
                            <pre className="text-xs bg-background rounded p-3 whitespace-pre-wrap max-h-40 overflow-auto font-mono border">
                              {run.user_prompt}
                            </pre>
                          </div>
                        )}

                        {/* AI Output */}
                        {run.output_markdown && (
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <Label className="text-xs font-bold">AI Output</Label>
                              <Button size="sm" variant="ghost" className="h-6 text-xs"
                                onClick={() => copyText(run.output_raw || run.output_markdown!, "AI Output")}>
                                <Copy className="h-3 w-3 mr-1" />複製
                              </Button>
                            </div>
                            <pre className="text-xs bg-background rounded p-3 whitespace-pre-wrap max-h-60 overflow-auto font-mono border">
                              {run.output_markdown}
                            </pre>
                          </div>
                        )}

                        {/* No prompt stored warning */}
                        {!run.system_prompt && !run.user_prompt && (
                          <div className="text-xs text-muted-foreground italic py-4 text-center">
                            此次執行未存儲完整 prompt（舊版本執行紀錄）
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
